import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Zap, Trash2, ToggleLeft, ToggleRight, Plus, Users, Shuffle, Building2, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const METHOD_CONFIG = {
  round_robin: { label: 'Round Robin', icon: Shuffle, desc: 'Distribute deals evenly across the team', color: 'bg-blue-100 text-blue-700' },
  business_type: { label: 'Business Type', icon: Building2, desc: 'Assign based on business type match', color: 'bg-purple-100 text-purple-700' },
  volume_range: { label: 'Volume Range', icon: DollarSign, desc: 'Assign based on monthly volume range', color: 'bg-emerald-100 text-emerald-700' },
};

const PIPELINES = {
  merchant_services: 'Merchant Services',
  lending: 'Lending',
  pos: 'POS Systems',
};

export default function AssignmentRulesDialog({ open, onClose, pipelineType }) {
  const { user } = useAuthStore();
  const [rules, setRules] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const loadRules = useCallback(async () => {
    try {
      const params = pipelineType ? `?pipeline=${pipelineType}` : '';
      const data = await api.request(`/pipeline/assignment-rules${params}`);
      setRules(data || []);
    } catch {}
    setLoading(false);
  }, [pipelineType]);

  const loadOrgUsers = useCallback(async () => {
    try {
      const data = await api.request('/pipeline/org-users');
      setOrgUsers(data || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (open) { loadRules(); loadOrgUsers(); }
  }, [open, loadRules, loadOrgUsers]);

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this assignment rule?')) return;
    try {
      await api.request(`/pipeline/assignment-rules/${ruleId}`, { method: 'DELETE' });
      toast.success('Rule deleted');
      loadRules();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggle = async (ruleId) => {
    try {
      const result = await api.request(`/pipeline/assignment-rules/${ruleId}/toggle`, { method: 'PUT' });
      toast.success(result.active ? 'Rule activated' : 'Rule deactivated');
      loadRules();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="assignment-rules-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            Assignment Rules {pipelineType && `— ${PIPELINES[pipelineType] || pipelineType}`}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3">
          {loading ? (
            <div className="text-center py-8 text-sm text-muted-foreground">Loading...</div>
          ) : rules.length === 0 ? (
            <div className="text-center py-8">
              <Zap className="h-10 w-10 mx-auto text-muted-foreground/20 mb-2" />
              <p className="text-sm text-muted-foreground">No assignment rules yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Create rules to auto-assign deals to your team.</p>
            </div>
          ) : (
            rules.map(rule => {
              const methodCfg = METHOD_CONFIG[rule.method] || METHOD_CONFIG.round_robin;
              const Icon = methodCfg.icon;
              return (
                <div key={rule.id} className={`rounded-lg border p-3 transition-all ${rule.active ? 'border-border bg-card' : 'border-dashed border-muted-foreground/20 bg-muted/30 opacity-60'}`}
                  data-testid={`rule-card-${rule.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${methodCfg.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{rule.name}</p>
                        <p className="text-xs text-muted-foreground">{methodCfg.desc}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button onClick={() => handleToggle(rule.id)} className="p-1 rounded hover:bg-muted" data-testid={`toggle-rule-${rule.id}`}>
                        {rule.active ? <ToggleRight className="w-5 h-5 text-primary" /> : <ToggleLeft className="w-5 h-5 text-muted-foreground" />}
                      </button>
                      <button onClick={() => handleDelete(rule.id)} className="p-1 rounded hover:bg-red-50 text-red-500" data-testid={`delete-rule-${rule.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Criteria */}
                  {rule.method === 'business_type' && rule.criteria?.business_type && (
                    <div className="mt-2 text-xs"><span className="text-muted-foreground">Match:</span> <Badge variant="secondary">{rule.criteria.business_type}</Badge></div>
                  )}
                  {rule.method === 'volume_range' && (
                    <div className="mt-2 text-xs"><span className="text-muted-foreground">Range:</span> ${rule.criteria?.min_volume?.toLocaleString() || 0} — ${rule.criteria?.max_volume?.toLocaleString() || '∞'}</div>
                  )}

                  {/* Assigned Users */}
                  <div className="mt-2 flex items-center gap-1 flex-wrap">
                    <Users className="w-3 h-3 text-muted-foreground" />
                    {(rule.assigned_user_ids || []).map(uid => {
                      const u = orgUsers.find(ou => ou.id === uid);
                      return <Badge key={uid} variant="outline" className="text-[10px]">{u?.name || uid.slice(-6)}</Badge>;
                    })}
                    <span className="text-[10px] text-muted-foreground ml-1">Priority: {rule.priority}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => setShowCreate(true)} data-testid="add-rule-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Rule
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Create Rule Sub-Dialog */}
      <CreateRuleDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        pipelineType={pipelineType}
        orgUsers={orgUsers}
        onCreated={() => { loadRules(); setShowCreate(false); }}
      />
    </Dialog>
  );
}

function CreateRuleDialog({ open, onClose, pipelineType, orgUsers, onCreated }) {
  const [name, setName] = useState('');
  const [pipeline, setPipeline] = useState(pipelineType || 'merchant_services');
  const [method, setMethod] = useState('round_robin');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [businessType, setBusinessType] = useState('');
  const [minVolume, setMinVolume] = useState('');
  const [maxVolume, setMaxVolume] = useState('');
  const [priority, setPriority] = useState('10');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (pipelineType) setPipeline(pipelineType); }, [pipelineType]);

  const toggleUser = (uid) => {
    setSelectedUsers(prev => prev.includes(uid) ? prev.filter(u => u !== uid) : [...prev, uid]);
  };

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Rule name required'); return; }
    if (selectedUsers.length === 0) { toast.error('Select at least one user'); return; }

    setSaving(true);
    try {
      const criteria = {};
      if (method === 'business_type') criteria.business_type = businessType;
      if (method === 'volume_range') {
        criteria.min_volume = parseFloat(minVolume) || 0;
        criteria.max_volume = parseFloat(maxVolume) || 999999999;
      }

      await api.request('/pipeline/assignment-rules', {
        method: 'POST',
        body: JSON.stringify({
          name, pipeline, method, criteria,
          assigned_user_ids: selectedUsers,
          priority: parseInt(priority) || 10,
        }),
      });
      toast.success('Rule created');
      onCreated();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const BUSINESS_TYPES = ['Retail', 'Restaurant', 'B2B', 'E-Commerce', 'Healthcare', 'Hospitality', 'Professional Services'];

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="create-rule-dialog">
        <DialogHeader>
          <DialogTitle>Create Assignment Rule</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 max-h-[55vh] overflow-y-auto">
          <div>
            <Label className="text-xs">Rule Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Restaurant Specialist" className="mt-1" data-testid="rule-name-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Pipeline</Label>
              <Select value={pipeline} onValueChange={setPipeline}>
                <SelectTrigger className="mt-1" data-testid="rule-pipeline-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PIPELINES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="mt-1" data-testid="rule-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(METHOD_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Criteria fields */}
          {method === 'business_type' && (
            <div>
              <Label className="text-xs">Business Type Match</Label>
              <Select value={businessType} onValueChange={setBusinessType}>
                <SelectTrigger className="mt-1" data-testid="rule-biz-type-select"><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {method === 'volume_range' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Min Volume ($)</Label>
                <Input type="number" value={minVolume} onChange={e => setMinVolume(e.target.value)} placeholder="0" className="mt-1" data-testid="rule-min-vol" />
              </div>
              <div>
                <Label className="text-xs">Max Volume ($)</Label>
                <Input type="number" value={maxVolume} onChange={e => setMaxVolume(e.target.value)} placeholder="999999" className="mt-1" data-testid="rule-max-vol" />
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">Priority (higher = checked first)</Label>
            <Input type="number" value={priority} onChange={e => setPriority(e.target.value)} className="mt-1" data-testid="rule-priority-input" />
          </div>

          {/* User Selection */}
          <div>
            <Label className="text-xs">Assign To (select users)</Label>
            <div className="mt-1.5 space-y-1 max-h-32 overflow-y-auto border rounded-lg p-2">
              {orgUsers.filter(u => u.role !== 'super_admin').map(u => (
                <label key={u.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition-colors ${
                  selectedUsers.includes(u.id) ? 'bg-primary/10' : 'hover:bg-muted'
                }`} data-testid={`rule-user-${u.id}`}>
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(u.id)}
                    onChange={() => toggleUser(u.id)}
                    className="rounded"
                  />
                  <span className="flex-1">{u.name}</span>
                  <span className="text-[10px] text-muted-foreground">{u.role}</span>
                </label>
              ))}
            </div>
            {selectedUsers.length > 0 && (
              <p className="text-[10px] text-muted-foreground mt-1">{selectedUsers.length} user(s) selected</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={saving} data-testid="save-rule-btn">
            {saving ? 'Creating...' : 'Create Rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
